import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { Activity, MoreHorizontal, Pencil, UserMinus, UserPlus, Users } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Avatar,
  DataTable,
  DetailList,
  DetailSection,
  DetailSheet,
  EmptyState,
  Pagination,
  Panel,
  SearchInput,
  Segmented,
  SkeletonRows,
  StatCard,
  StatGrid,
  Tag,
  Toolbar,
  fmtDateBR,
  relativeDays,
  rowClass,
  td,
  th,
  usePagination,
  type Tone,
} from "@/components/superadmin/kit"
import { useCondominiums } from "@/hooks/useCondominiums"
import { useDeleteUser } from "@/hooks/useEditUser"
import { useProfiles } from "@/hooks/useProfiles"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { cn } from "@/lib/utils"

import { CreateUserModal } from "./CreateUserModal"
import { EditUserModal } from "./EditUserModal"

type Profile = Tables<"profiles">

export const ROLE_META: Record<string, { label: string; tone: Tone }> = {
  superadmin: { label: "Super Admin", tone: "violet" },
  administracao: { label: "Administração", tone: "accent" },
  sindico: { label: "Síndico", tone: "accent" },
  porteiro: { label: "Porteiro", tone: "warning" },
  morador: { label: "Morador", tone: "neutral" },
}

const RESIDENT_TYPE: Record<string, string> = { owner: "Proprietário", tenant: "Inquilino", both: "Proprietário e inquilino" }

interface UserAccess {
  user_id: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
}

/** Último acesso vem de auth.users via RPC; sem a migration aplicada, retorna null. */
function useUserAccess() {
  return useQuery({
    queryKey: ["superadmin-user-access"],
    queryFn: async (): Promise<Map<string, UserAccess> | null> => {
      const { data, error } = await (supabase.rpc as unknown as (fn: string) => Promise<{ data: UserAccess[] | null; error: unknown }>)("superadmin_user_access")
      if (error || !data) return null
      return new Map(data.map((row) => [row.user_id, row]))
    },
    staleTime: 60_000,
  })
}

function maskCpf(cpf?: string | null) {
  if (!cpf) return null
  const d = cpf.replace(/\D/g, "")
  return d.length === 11 ? `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**` : cpf
}

type StatusFilter = "all" | "active" | "inactive"

const UserManagement = () => {
  const { data: profiles = [], isLoading } = useProfiles()
  const { data: condominiums = [] } = useCondominiums()
  const { data: access } = useUserAccess()
  const deactivate = useDeleteUser()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Profile | null>(null)
  const [viewing, setViewing] = useState<Profile | null>(null)
  const [confirming, setConfirming] = useState<Profile | null>(null)
  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<StatusFilter>("all")
  const [role, setRole] = useState("all")
  const [project, setProject] = useState("all")
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get("novo") !== "1") return
    setCreateOpen(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const orgName = useMemo(() => new Map(condominiums.map((c) => [c.id, c.name])), [condominiums])
  const hasAccess = !!access

  const counts = useMemo(() => ({
    all: profiles.length,
    active: profiles.filter((p) => p.is_active !== false).length,
    inactive: profiles.filter((p) => p.is_active === false).length,
  }), [profiles])

  const recentLogins = useMemo(() => {
    if (!access) return null
    const limit = Date.now() - 7 * 86_400_000
    return profiles.filter((p) => {
      const at = access.get(p.id)?.last_sign_in_at
      return at && new Date(at).getTime() >= limit
    }).length
  }, [access, profiles])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return profiles
      .filter((p) => {
        const matchesQuery = !q || [p.full_name, p.email, p.phone].some((v) => String(v || "").toLowerCase().includes(q))
        const matchesStatus = status === "all" || (status === "active" ? p.is_active !== false : p.is_active === false)
        const matchesRole = role === "all" || p.role === role
        const matchesProject = project === "all" || (project === "none" ? !p.condominium_id : p.condominium_id === project)
        return matchesQuery && matchesStatus && matchesRole && matchesProject
      })
      .sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email, "pt-BR"))
  }, [profiles, query, status, role, project])

  const pager = usePagination(filtered, 15)
  useEffect(() => pager.reset(), [query, status, role, project]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: activity = [], isLoading: loadingActivity } = useQuery({
    queryKey: ["superadmin-user-activity", viewing?.id],
    enabled: !!viewing,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("id, action, table_name, created_at")
        .eq("user_id", viewing!.id)
        .order("created_at", { ascending: false })
        .limit(8)
      if (error) return []
      return data ?? []
    },
  })

  const lastAccess = (p: Profile) => {
    if (!hasAccess) return <span className="text-muted-foreground">—</span>
    const at = access?.get(p.id)?.last_sign_in_at
    if (!at) return <span className="text-muted-foreground">Nunca acessou</span>
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="tabular-nums text-foreground">{relativeDays(at)}</span>
        </TooltipTrigger>
        <TooltipContent>{fmtDateBR(at, true)}</TooltipContent>
      </Tooltip>
    )
  }

  const viewingAccess = viewing ? access?.get(viewing.id) : undefined
  const viewingRole = viewing ? ROLE_META[viewing.role] ?? { label: viewing.role, tone: "neutral" as Tone } : null

  return (
    <>
      <StatGrid>
        <StatCard label="Usuários" value={counts.all} hint={`${counts.active} ativos`} icon={Users} tone="accent" />
        <StatCard label="Inativos" value={counts.inactive} hint={counts.inactive ? "Sem acesso ao sistema" : "Todos com acesso"} icon={UserMinus} tone={counts.inactive ? "warning" : "neutral"} />
        <StatCard
          label="Acessaram nos últimos 7 dias"
          value={recentLogins ?? "—"}
          hint={hasAccess ? `de ${counts.active} ativos` : "Aguardando migration de acessos"}
          icon={Activity}
          tone="success"
          info={hasAccess ? undefined : "Aplique a migration 20261015090000_superadmin_user_access.sql no Supabase para ver último acesso."}
        />
        <StatCard label="Administração da plataforma" value={profiles.filter((p) => !p.condominium_id).length} hint="Sem organização vinculada" icon={UserPlus} />
      </StatGrid>

      <Panel flush>
        <Toolbar>
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: "Todos", count: counts.all },
              { value: "active", label: "Ativos", count: counts.active },
              { value: "inactive", label: "Inativos", count: counts.inactive },
            ]}
          />
          <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-8 w-full text-[13px] sm:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os perfis</SelectItem>
                {Object.entries(ROLE_META).map(([value, meta]) => <SelectItem key={value} value={value}>{meta.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={project} onValueChange={setProject}>
              <SelectTrigger className="h-8 w-full text-[13px] sm:w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as organizações</SelectItem>
                <SelectItem value="none">Administração da plataforma</SelectItem>
                {condominiums.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <SearchInput value={query} onChange={setQuery} placeholder="Nome, e-mail ou telefone" className="lg:w-60" />
          </div>
        </Toolbar>

        {isLoading ? (
          <SkeletonRows rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Users} title="Nenhum usuário encontrado" description="Ajuste a busca ou os filtros." />
        ) : (
          <>
            <DataTable minWidth={900}>
              <thead>
                <tr className="border-b border-border">
                  <th className={th}>Usuário</th>
                  <th className={th}>Perfil</th>
                  <th className={th}>Organização</th>
                  <th className={th}>Último acesso</th>
                  <th className={th}>Status</th>
                  <th className={th}>Criado em</th>
                  <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {pager.slice.map((p) => {
                  const meta = ROLE_META[p.role] ?? { label: p.role, tone: "neutral" as Tone }
                  return (
                    <tr key={p.id} className={rowClass} onClick={() => setViewing(p)}>
                      <td className={td}>
                        <div className="flex items-center gap-3">
                          <Avatar name={p.full_name || p.email} />
                          <div className="min-w-0">
                            <div className="max-w-[240px] truncate text-[13px] font-medium text-foreground">{p.full_name || "Sem nome"}</div>
                            <div className="max-w-[240px] truncate text-[12px] text-muted-foreground">{p.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className={td}><Tag tone={meta.tone}>{meta.label}</Tag></td>
                      <td className={cn(td, "max-w-[200px] truncate text-[13px]", p.condominium_id ? "text-foreground" : "text-muted-foreground")}>
                        {p.condominium_id ? orgName.get(p.condominium_id) ?? "Organização removida" : "Plataforma"}
                      </td>
                      <td className={cn(td, "text-[13px]")}>{lastAccess(p)}</td>
                      <td className={td}>{p.is_active === false ? <Tag tone="neutral" dot>Inativo</Tag> : <Tag tone="success" dot>Ativo</Tag>}</td>
                      <td className={cn(td, "text-[13px] tabular-nums text-muted-foreground")}>{fmtDateBR(p.created_at)}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onSelect={() => setViewing(p)}><Users className="mr-2 h-4 w-4" />Ver detalhes</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => setEditing(p)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                            {p.is_active !== false && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={() => setConfirming(p)} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><UserMinus className="mr-2 h-4 w-4" />Desativar</DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </DataTable>
            <Pagination {...pager} noun="usuários" />
          </>
        )}
      </Panel>

      <DetailSheet
        open={!!viewing}
        onOpenChange={(o) => !o && setViewing(null)}
        badge={viewing && (
          <div className="flex items-center gap-3">
            <Avatar name={viewing.full_name || viewing.email} className="h-11 w-11 text-[13px]" />
            <div className="flex flex-wrap gap-1.5">
              {viewingRole && <Tag tone={viewingRole.tone}>{viewingRole.label}</Tag>}
              {viewing.is_active === false ? <Tag tone="neutral" dot>Inativo</Tag> : <Tag tone="success" dot>Ativo</Tag>}
            </div>
          </div>
        )}
        title={viewing?.full_name || "Sem nome"}
        subtitle={viewing?.email}
        actions={viewing && (
          <>
            <Button size="sm" className="h-8 gap-1.5" onClick={() => { setEditing(viewing); setViewing(null) }}><Pencil className="h-3.5 w-3.5" />Editar</Button>
            {viewing.is_active !== false && (
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-[hsl(var(--destructive))]" onClick={() => setConfirming(viewing)}><UserMinus className="h-3.5 w-3.5" />Desativar</Button>
            )}
          </>
        )}
      >
        {viewing && (
          <>
            <DetailSection title="Acesso">
              <DetailList items={[
                ["Último acesso", hasAccess ? (viewingAccess?.last_sign_in_at ? `${fmtDateBR(viewingAccess.last_sign_in_at, true)} (${relativeDays(viewingAccess.last_sign_in_at)})` : "Nunca acessou") : <span key="na" className="text-muted-foreground">Disponível após aplicar a migration de acessos</span>],
                ["E-mail confirmado", hasAccess ? (viewingAccess?.email_confirmed_at ? fmtDateBR(viewingAccess.email_confirmed_at, true) : "Não confirmado") : null],
                ["Conta criada", fmtDateBR(viewing.created_at, true)],
                ["Última alteração", fmtDateBR(viewing.updated_at, true)],
              ]} />
            </DetailSection>

            <DetailSection title="Contato">
              <DetailList items={[
                ["E-mail", viewing.email],
                ["Telefone", viewing.phone],
                ["WhatsApp", viewing.whatsapp_phone],
                ["CPF", maskCpf(viewing.cpf)],
              ]} />
            </DetailSection>

            <DetailSection title="Vínculo">
              <DetailList items={[
                ["Organização", viewing.condominium_id ? orgName.get(viewing.condominium_id) ?? "Organização removida" : "Administração da plataforma"],
                ["Bloco / torre", viewing.building],
                ["Apartamento", viewing.apartment_number],
                ["Tipo de morador", viewing.resident_type ? RESIDENT_TYPE[viewing.resident_type] : null],
              ]} />
            </DetailSection>

            <DetailSection title="Atividade recente">
              {loadingActivity ? (
                <div className="h-16 animate-pulse rounded-lg bg-[hsl(228_8%_10%)]" />
              ) : activity.length === 0 ? (
                <p className="rounded-lg border border-border p-3 text-[13px] text-muted-foreground">Nenhuma ação registrada no log de auditoria.</p>
              ) : (
                <ol className="relative space-y-3 border-l border-border pl-5">
                  {activity.map((ev) => (
                    <li key={ev.id} className="relative">
                      <span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-[hsl(228_11%_6.5%)] bg-[hsl(225_5%_45%)]" />
                      <div className="text-[13px] text-foreground">{ev.action} <span className="text-muted-foreground">em {ev.table_name}</span></div>
                      <div className="text-[11px] tabular-nums text-muted-foreground">{fmtDateBR(ev.created_at, true)}</div>
                    </li>
                  ))}
                </ol>
              )}
            </DetailSection>
          </>
        )}
      </DetailSheet>

      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar {confirming?.full_name || confirming?.email}?</AlertDialogTitle>
            <AlertDialogDescription>A pessoa perde o acesso ao sistema. O cadastro e o histórico continuam guardados e podem ser reativados na edição.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deactivate.isPending}
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              onClick={async () => {
                if (!confirming) return
                await deactivate.mutateAsync(confirming.id)
                setViewing(null)
              }}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateUserModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
      <EditUserModal user={editing} isOpen={!!editing} onClose={() => setEditing(null)} />
    </>
  )
}

export { UserManagement }
